import { logger } from "@/utils/logger";
/**
 * Utility to detect and handle duplicate keys in JSON strings
 * This catches issues where OpenAI's strict mode fails to prevent duplicate keys
 */

export interface DuplicateKeyInfo {
  hasDuplicates: boolean;
  duplicateKeys: string[];
  locations: { key: string; count: number }[];
}

/**
 * Detects duplicate keys in a JSON string before parsing
 * This is critical because JSON.parse() silently uses the last value when duplicates exist
 * Only flags keys that appear multiple times in the SAME object, not across array items
 */
export const detectDuplicateKeys = (jsonString: string): DuplicateKeyInfo => {
  const result: DuplicateKeyInfo = {
    hasDuplicates: false,
    duplicateKeys: [],
    locations: []
  };

  try {
    // Track nesting depth and object scopes to properly detect duplicates
    let depth = 0;
    let inString = false;
    let escape = false;
    let currentObjectKeys = new Map<number, Set<string>>(); // depth -> keys in that object
    let objectKeyPositions = new Map<string, number[]>(); // "depth:key" -> positions
    
    let i = 0;
    while (i < jsonString.length) {
      const char = jsonString[i];
      
      if (escape) {
        escape = false;
        i++;
        continue;
      }
      
      if (char === '\\') {
        escape = true;
        i++;
        continue;
      }
      
      if (char === '"') {
        inString = !inString;
        i++;
        continue;
      }
      
      if (inString) {
        i++;
        continue;
      }
      
      // Track depth changes
      if (char === '{') {
        depth++;
        if (!currentObjectKeys.has(depth)) {
          currentObjectKeys.set(depth, new Set());
        }
      } else if (char === '}') {
        // Clear keys for this depth when object closes
        currentObjectKeys.delete(depth);
        depth--;
      } else if (char === '[') {
        depth++;
      } else if (char === ']') {
        depth--;
      }
      
      // Detect keys at current depth
      if (char === '"' && !inString) {
        const keyStart = i + 1;
        let keyEnd = keyStart;
        let keyEscape = false;
        
        // Find the end of the key
        while (keyEnd < jsonString.length) {
          if (keyEscape) {
            keyEscape = false;
            keyEnd++;
            continue;
          }
          if (jsonString[keyEnd] === '\\') {
            keyEscape = true;
            keyEnd++;
            continue;
          }
          if (jsonString[keyEnd] === '"') {
            break;
          }
          keyEnd++;
        }
        
        // Check if this is actually a key (followed by :)
        let colonPos = keyEnd + 1;
        while (colonPos < jsonString.length && /\s/.test(jsonString[colonPos])) {
          colonPos++;
        }
        
        if (colonPos < jsonString.length && jsonString[colonPos] === ':') {
          const key = jsonString.substring(keyStart, keyEnd);
          const scopeKey = `${depth}:${key}`;
          
          // Check if this key already exists at this depth
          if (currentObjectKeys.get(depth)?.has(key)) {
            result.hasDuplicates = true;
            if (!result.duplicateKeys.includes(key)) {
              result.duplicateKeys.push(key);
            }
            
            const positions = objectKeyPositions.get(scopeKey) || [];
            positions.push(i);
            objectKeyPositions.set(scopeKey, positions);
            
            // Update or add location info
            const existingLocation = result.locations.find(loc => loc.key === key);
            if (existingLocation) {
              existingLocation.count++;
            } else {
              result.locations.push({ key, count: positions.length });
            }
          } else {
            // Add key to current object scope
            currentObjectKeys.get(depth)?.add(key);
            objectKeyPositions.set(scopeKey, [i]);
          }
          
          i = keyEnd;
        }
      }
      
      i++;
    }

    return result;
  } catch (error) {
    console.error('Error detecting duplicate keys:', error);
    return result;
  }
};

/**
 * Removes duplicate keys from a JSON string by keeping only the LAST occurrence
 * This matches the behavior of JSON.parse if it were to succeed
 */
export const removeDuplicateKeys = (jsonString: string): string => {
  try {
    // Strategy: Try to extract just the first valid JSON object
    // This handles cases where there's extra content after the JSON
    
    let depth = 0;
    let inString = false;
    let escape = false;
    let jsonEnd = -1;
    
    // Find where the first complete JSON object ends
    for (let i = 0; i < jsonString.length; i++) {
      const char = jsonString[i];
      
      if (escape) {
        escape = false;
        continue;
      }
      
      if (char === '\\') {
        escape = true;
        continue;
      }
      
      if (char === '"') {
        inString = !inString;
        continue;
      }
      
      if (inString) continue;
      
      if (char === '{' || char === '[') {
        depth++;
      } else if (char === '}' || char === ']') {
        depth--;
        if (depth === 0) {
          jsonEnd = i + 1;
          break;
        }
      }
    }
    
    // Extract just the first complete JSON object
    let extractedJSON = jsonEnd > 0 ? jsonString.substring(0, jsonEnd) : jsonString;
    
    logger.log('🔍 Extracted JSON length:', extractedJSON.length, 'vs original:', jsonString.length);
    
    // Now try to parse and rebuild without duplicates using a reviver approach
    // Parse once to get the structure, then stringify to remove duplicates
    try {
      const parsed = JSON.parse(extractedJSON);
      const cleaned = JSON.stringify(parsed);
      logger.log('✅ Successfully cleaned and extracted valid JSON');
      return cleaned;
    } catch (parseError) {
      console.error('❌ Extracted JSON still has errors, trying line-by-line approach');
      // Fallback: original line-by-line approach
      return extractedJSON;
    }
  } catch (error) {
    console.error('❌ Failed to remove duplicate keys:', error);
    return jsonString;
  }
};

/**
 * Validates JSON string and logs issues
 */
export const validateAndLogJSON = (jsonString: string, source: string = 'unknown'): void => {
  const duplicateInfo = detectDuplicateKeys(jsonString);
  
  if (duplicateInfo.hasDuplicates) {
    console.error(`❌ JSON VALIDATION FAILED from ${source}:`);
    console.error('Duplicate keys found:', duplicateInfo.duplicateKeys);
    console.error('Key occurrences:', duplicateInfo.locations);
    console.error('This indicates OpenAI\'s strict mode failed to enforce schema constraints');
    
    // Log the problematic section (first 500 chars around first duplicate)
    if (duplicateInfo.locations.length > 0) {
      const firstDuplicateKey = duplicateInfo.locations[0].key;
      const keyIndex = jsonString.indexOf(`"${firstDuplicateKey}":`);
      if (keyIndex !== -1) {
        const start = Math.max(0, keyIndex - 100);
        const end = Math.min(jsonString.length, keyIndex + 400);
        console.error('Context:', jsonString.substring(start, end));
      }
    }
  }
};
